/**
 * @fileoverview The row under a new photo's card: Cancel on the left and the Add button on the right.
 *
 * Cancel is the quieter button, first in the row (the usual phone order), the same height as Add; Add comes second and
 * takes the spare width. It does what the card hands it: for a plant card, what the photo's × does (B107); for the seed-packet card in "Check and save",
 * the sheet's close, which keeps the photo (B137 F1).
 *
 * A long action word wraps inside its button instead of being cut off at 375 px (the Spanish "Guardar 1 en el banco", "Add 1 plant to My Garden"):
 * the action keeps its 44px height, takes small side padding (px-3, not the base button's px-8) and sets two lines of snug text inside it, so nothing is cut off; Cancel is flex-1 with px-4 and the action flex-1 grow-[1.4] (Tailwind orders grow after flex, so it wins), so the action is always the wider, as in ActionRow.
 *
 * Where: PileCard (the plant question), KindsPile, PacketPile (today's plant card) and PacketKeep (the Seed vault card): their Add / Save buttons.
 *
 * @module AddCancelRow
 */
import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** AddCancelRow's props. */
interface AddCancelRowProps {
  /** The Add button itself (it keeps its own label, spinner and disabled state); it comes second and takes the room Cancel leaves. */
  children: React.ReactNode;
  /** Cancel: does what the photo's × does. When absent, only the Add button is drawn. */
  onCancel?: () => void;
  /** True while the card is busy saving or elsewhere: Cancel waits, as the × does. */
  disabled?: boolean;
}

/**
 * Cancel with the Add button after it.
 *
 * @param props - AddCancelRowProps.
 */
export const AddCancelRow: React.FC<AddCancelRowProps> = ({ children, onCancel, disabled = false }) => {
  const { reactTranslate: t } = useTranslation();
  if (!onCancel) return <>{children}</>;
  return (
    <div className="flex gap-2 [&>*:first-child]:flex-1 [&>*:first-child]:px-4 [&>*:last-child]:px-3 [&>*:last-child]:min-w-0 [&>*:last-child]:whitespace-normal [&>*:last-child]:py-0 [&>*:last-child]:leading-tight [&>*:last-child]:grow-[1.4] [&>*:last-child]:flex-1">
      <Button type="button" variant="outline" size="lg" disabled={disabled} onClick={onCancel}>{t('tray.pile.cancel')}</Button>
      {children}
    </div>
  );
};
